import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Heart, Plus, Calendar, MapPin, Users, Bell, LogOut,
  Settings, Eye, Trash2, MoreVertical, ExternalLink, Sparkles, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

export default function DashboardPage() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  const [newEvent, setNewEvent] = useState({
    title: '',
    event_date: '',
    event_time: '',
    venue_name: '',
    venue_address: '',
    design_template: 'elegant'
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async (eventId) => {
    try {
      const response = await api.get(`/events/${eventId}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.event_date || !newEvent.event_time || !newEvent.venue_name) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/events', newEvent);
      setEvents([...events, response.data]);
      setShowCreateDialog(false);
      setNewEvent({
        title: '',
        event_date: '',
        event_time: '',
        venue_name: '',
        venue_address: '',
        design_template: 'elegant'
      });
      toast.success('¡Evento creado exitosamente!');
      navigate(`/event/${response.data.id}`);
    } catch (error) {
      toast.error('Error al crear evento');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('¿Estás seguro de eliminar este evento?')) return;

    try {
      await api.delete(`/events/${eventId}`);
      setEvents(events.filter(e => e.id !== eventId));
      toast.success('Evento eliminado');
    } catch (error) {
      toast.error('Error al eliminar evento');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInvitationUrl = (event) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/invite/${event.public_token}`;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-wedding-background">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-wedding-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-wedding-primary" fill="currentColor" />
            <span className="font-heading text-xl text-wedding-text">Wedvites</span>
          </Link>

          <div className="flex items-center gap-4">
            {isAdmin && (
              <Link to="/admin">
                <Button variant="ghost" className="text-wedding-primary hover:bg-wedding-accent flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Panel Admin
                </Button>
              </Link>
            )}
            <Link to="/pricing">
              <Button variant="outline" className="text-wedding-primary border-wedding-primary hover:bg-wedding-accent hidden sm:flex">
                Mejorar Plan
              </Button>
            </Link>
            <span className="text-wedding-text-muted text-sm hidden sm:block">
              Hola, {user?.name} ({user?.role})
            </span>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-wedding-text-muted hover:text-wedding-primary"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl text-wedding-text">Mis Eventos</h1>
            <p className="text-wedding-text-muted mt-1">Gestiona tus invitaciones digitales</p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-wedding-primary hover:bg-wedding-primary-dark text-white rounded-full px-6"
            data-testid="create-event-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuevo Evento
          </Button>
        </div>

        {/* Upgrade Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-wedding-primary/10 to-wedding-accent/30 border border-wedding-primary/20 rounded-2xl p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-white rounded-full flex items-center justify-center shadow-sm">
              <Sparkles className="h-6 w-6 text-wedding-primary" />
            </div>
            <div>
              <h3 className="font-heading text-lg text-wedding-text">Eleva tu experiencia premium</h3>
              <p className="text-wedding-text-muted text-sm">Desbloquea invitados ilimitados, galería de fotos y música de fondo para tu boda.</p>
            </div>
          </div>
          <Link to="/pricing">
            <Button className="bg-wedding-primary hover:bg-wedding-primary-dark text-white rounded-full">
              Ver Planes Premium
            </Button>
          </Link>
        </motion.div>

        {/* Events Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-48 bg-wedding-accent rounded-t-lg"></div>
                <CardContent className="p-6">
                  <div className="h-6 bg-wedding-accent rounded w-2/3 mb-4"></div>
                  <div className="h-4 bg-wedding-accent rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : events.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-24 h-24 bg-wedding-accent rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-wedding-primary" />
            </div>
            <h2 className="font-heading text-2xl text-wedding-text mb-2">
              Crea tu primer evento
            </h2>
            <p className="text-wedding-text-muted mb-6 max-w-md mx-auto">
              Comienza a diseñar tu invitación de boda digital perfecta
            </p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-wedding-primary hover:bg-wedding-primary-dark text-white rounded-full px-8"
              data-testid="create-first-event-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Crear Evento
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="group overflow-hidden hover:shadow-lg transition-all duration-300" data-testid={`event-card-${index}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={event.hero_image || "https://images.unsplash.com/photo-1761211488173-a7154314420a?w=600&q=80"}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-heading text-xl text-white">{event.title}</h3>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-white/20 hover:bg-white/40 text-white"
                          data-testid={`event-menu-${index}`}
                        >
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => window.open(getInvitationUrl(event), '_blank')}>
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Ver Invitación
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDeleteEvent(event.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-wedding-text-muted text-sm mb-2">
                      <Calendar className="w-4 h-4" />
                      <span>{event.event_date} - {event.event_time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-wedding-text-muted text-sm mb-4">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">{event.venue_name}</span>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/event/${event.id}`} className="flex-1">
                        <Button
                          variant="outline"
                          className="w-full border-wedding-primary text-wedding-primary hover:bg-wedding-primary hover:text-white"
                          data-testid={`edit-event-${index}`}
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Editar
                        </Button>
                      </Link>
                      <Link to={`/event/${event.id}/guests`} className="flex-1">
                        <Button
                          className="w-full bg-wedding-primary hover:bg-wedding-primary-dark text-white"
                          data-testid={`guests-event-${index}`}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          Invitados
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Create Event Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Nuevo Evento</DialogTitle>
            <DialogDescription>
              Crea una nueva invitación digital para tu boda
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Título del Evento *</Label>
              <Input
                id="title"
                placeholder="Ej: Boda de María y Juan"
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                data-testid="new-event-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Fecha *</Label>
                <Input
                  id="date"
                  type="date"
                  value={newEvent.event_date}
                  onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                  data-testid="new-event-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Hora *</Label>
                <Input
                  id="time"
                  type="time"
                  value={newEvent.event_time}
                  onChange={(e) => setNewEvent({ ...newEvent, event_time: e.target.value })}
                  data-testid="new-event-time"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue">Lugar del Evento *</Label>
              <Input
                id="venue"
                placeholder="Ej: Hotel Grand Palace"
                value={newEvent.venue_name}
                onChange={(e) => setNewEvent({ ...newEvent, venue_name: e.target.value })}
                data-testid="new-event-venue"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Dirección</Label>
              <Input
                id="address"
                placeholder="Ej: Av. Principal 123, Ciudad"
                value={newEvent.venue_address}
                onChange={(e) => setNewEvent({ ...newEvent, venue_address: e.target.value })}
                data-testid="new-event-address"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="template">Plantilla de Diseño</Label>
              <Select
                value={newEvent.design_template}
                onValueChange={(value) => setNewEvent({ ...newEvent, design_template: value })}
              >
                <SelectTrigger data-testid="new-event-template">
                  <SelectValue placeholder="Selecciona una plantilla" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="elegant">Elegante Clásico</SelectItem>
                  <SelectItem value="modern">Moderno Minimalista</SelectItem>
                  <SelectItem value="romantic">Romántico Natural</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateEvent}
              disabled={creating}
              className="bg-wedding-primary hover:bg-wedding-primary-dark"
              data-testid="confirm-create-event"
            >
              {creating ? 'Creando...' : 'Crear Evento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
